import { useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { DataTable } from "../../components/ui/dataTable/DataTable";
import { Pagination } from "../../components/ui/pagination/Pagination";
import { StatusBadge } from "../../components/ui/statusBadge/StatusBadge";
import { EmptyState } from "../../components/ui/emptyState/EmptyState";
import { useModal } from "../../components/ui/modal/ModalProvider";
import { useToast } from "../../components/ui/toast/ToastProvider";
import { MOCK_DATA, bumpMockData, useMockDataVersion } from "../../data/mockDataStore";
import { date, money } from "../../utils/format";
import { ESTABLISHMENT_STATUS, ESTABLISHMENT_STATUS_OPTIONS } from "../../constants/establishmentStatus";
import { activityLabel } from "../../constants/establishmentActivity";
import { exportToCsv } from "../../utils/csv";
import { deriveEstablishmentKyc } from "../../utils/establishmentKyc";
import { isImageDoc, buildPlaceholderPdfDataUri, buildPlaceholderImageDataUri, downloadDoc } from "../../utils/docPreview";

const PAGE_SIZE = 8;

const organismeName = (e) =>
  MOCK_DATA.organismes.find((o) => o.id === e.organismeId)?.nom || "";

const statusLabel = (statut) =>
  ESTABLISHMENT_STATUS_OPTIONS.find((s) => s.value === statut)?.label || statut;

function exportEtablissements(rows) {
  const headers = ["Référence", "Nom", "Organisme", "Raison sociale", "Type", "Responsable", "Email", "Téléphone", "Adresse", "Statut", "Date d'inscription", "Dernière modification"];
  const csvRows = rows.map((e) => [
    e.id,
    e.nom,
    organismeName(e),
    e.raison,
    e.type,
    e.responsable,
    e.email,
    e.tel,
    e.adresse,
    statusLabel(e.statut),
    date(e.inscription),
    date(e.modifie),
  ]);
  exportToCsv(`etablissements-${new Date().toISOString().slice(0, 10)}.csv`, headers, csvRows);
}

export function Etablissements() {
  const { id } = useParams();
  if (id) return <EtablissementFiche id={id} />;
  return <EtablissementsList />;
}

function EtablissementsList() {
  useMockDataVersion();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [statut, setStatut] = useState("");
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const qq = q.toLowerCase();
    return MOCK_DATA.etablissements.filter((e) => {
      if (statut && e.statut !== statut) return false;
      if (qq && !(
        e.nom.toLowerCase().includes(qq)
        || e.id.toLowerCase().includes(qq)
        || e.responsable.toLowerCase().includes(qq)
        || organismeName(e).toLowerCase().includes(qq)
      )) return false;
      return true;
    });
  }, [q, statut]);

  const total = filtered.length;
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <>
      <div className="page-header">
        <div>
          <div className="breadcrumb">
            <a onClick={() => navigate("/dashboard")}>Dashboard</a>
            <i className="fa-solid fa-chevron-right"></i>Établissements
          </div>
          <h1 className="page-title">Établissements</h1>
          <div className="page-sub">Gérez l'onboarding et la validation des partenaires</div>
        </div>
        <div className="page-actions">
          <button className="btn" data-testid="btn-export-etab" onClick={() => exportEtablissements(filtered)}>
            <i className="fa-solid fa-download"></i> Exporter
          </button>
        </div>
      </div>

      <div className="card">
        <div className="toolbar">
          <div className="search">
            <i className="fa-solid fa-magnifying-glass"></i>
            <input
              data-testid="etab-search"
              placeholder="Rechercher par nom, organisme, référence, responsable…"
              value={q}
              onChange={(e) => { setQ(e.target.value); setPage(1); }}
            />
          </div>
          <select className="select" data-testid="etab-statut" value={statut} onChange={(e) => { setStatut(e.target.value); setPage(1); }}>
            <option value="">Tous les statuts</option>
            {ESTABLISHMENT_STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <select className="select" data-testid="etab-date">
            <option>Toutes dates</option>
            <option>7 derniers jours</option>
            <option>30 derniers jours</option>
            <option>90 derniers jours</option>
          </select>
          <div className="filter-chips">
            <span className="badge outline"><i className="fa-solid fa-filter"></i> {total} résultat{total > 1 ? "s" : ""}</span>
          </div>
        </div>

        <DataTable
          columns={[
            { label: "Référence", sortable: true, render: (e) => <span className="mono strong">{e.id}</span> },
            { label: "Nom", sortable: true, render: (e) => <div><div className="strong">{e.nom}</div><div className="muted">{e.type}</div></div> },
            { label: "Organisme", sortable: true, render: (e) => organismeName(e) || <span className="muted">—</span> },
            { label: "Statut", render: (e) => <StatusBadge statut={e.statut} /> },
            { label: "", render: (e) => <div className="actions-cell"><button className="icon-btn" data-testid={`row-open-${e.id}`} onClick={(ev) => { ev.stopPropagation(); navigate("/etablissements/" + e.id); }}><i className="fa-solid fa-arrow-right"></i></button></div> },
          ]}
          rows={paged}
          onRowClick={(e) => navigate("/etablissements/" + e.id)}
          rowTestIdPrefix="etab-row"
        />
        <Pagination total={total} page={page} pageSize={PAGE_SIZE} onPageChange={setPage} />
      </div>
    </>
  );
}

function EtablissementFiche({ id }) {
  useMockDataVersion();
  const navigate = useNavigate();
  const { openModal, confirm, closeModal } = useModal();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("entreprise");

  const e = MOCK_DATA.etablissements.find((x) => x.id === id);
  if (!e) return <EmptyState title="Établissement introuvable" sub={`Aucun établissement avec la référence ${id}`} />;

  const docs = e.docs.length ? e.docs : [
    { nom: "Extrait de registre (Modèle J)", type: "PDF", taille: "412 Ko", statut: "Conforme" },
    { nom: "Pièce d'identité représentant", type: "PDF", taille: "220 Ko", statut: "Conforme" },
  ];
  const kyc = deriveEstablishmentKyc(e);

  const balance = MOCK_DATA.balances.find((b) => b.etabId === e.id);
  const solde = balance?.solde || 0;
  const balanceClass = solde > 0 ? "positive" : solde < 0 ? "negative" : "zero";
  const balanceIcoBg = solde < 0 ? "var(--danger)" : solde > 0 ? "var(--success)" : "var(--neutral)";
  const balanceIco = solde < 0 ? "fa-arrow-down" : solde > 0 ? "fa-arrow-up" : "fa-equals";

  const activate = () => {
    confirm({
      title: "Activer l'établissement",
      message: <>Confirmez l'activation de <strong>{e.nom}</strong>. Le partenaire sera notifié par email.</>,
      confirmText: "Activer",
      type: "success",
      onConfirm: () => { e.statut = ESTABLISHMENT_STATUS.ACTIVATED; bumpMockData(); toast(`${e.nom} activé`, "success"); },
    });
  };

  const suspend = () => {
    confirm({
      title: "Suspendre l'établissement",
      message: <>Cette action masquera <strong>{e.nom}</strong> de la marketplace. Vous pouvez fournir un motif.</>,
      confirmText: "Suspendre",
      type: "danger",
      onConfirm: () => { e.statut = ESTABLISHMENT_STATUS.DISABLED; bumpMockData(); toast(`${e.nom} suspendu`, "warn"); },
    });
  };

  const openMessage = () => {
    openModal({
      title: "Envoyer un message à " + e.nom,
      body: (
        <>
          <div className="form-row"><label className="label">Sujet</label><input className="input" placeholder="Objet du message" /></div>
          <div className="form-row"><label className="label">Message</label><textarea className="textarea" placeholder="Votre message…"></textarea></div>
        </>
      ),
      footer: (
        <>
          <button className="btn" onClick={closeModal}>Annuler</button>
          <button className="btn btn-primary" onClick={() => { closeModal(); toast("Message envoyé", "success"); }}>Envoyer</button>
        </>
      ),
    });
  };

  const openRequestDoc = () => {
    openModal({
      title: "Demander un document",
      body: (
        <>
          <div className="form-row">
            <label className="label">Type de document</label>
            <select className="input">
              <option>Extrait de registre (Modèle J) récent</option>
              <option>Pièce d'identité représentant</option>
              <option>Licence tourisme</option>
              <option>Autre</option>
            </select>
          </div>
          <div className="form-row">
            <label className="label">Message au partenaire</label>
            <textarea className="textarea" defaultValue="Bonjour, merci de nous transmettre un document conforme dans les meilleurs délais."></textarea>
          </div>
        </>
      ),
      footer: (
        <>
          <button className="btn" onClick={closeModal}>Annuler</button>
          <button className="btn btn-primary" onClick={() => { closeModal(); toast("Demande envoyée", "success"); }}>Envoyer la demande</button>
        </>
      ),
    });
  };

  const viewDoc = (d) => {
    const image = isImageDoc(d);
    const src = image ? buildPlaceholderImageDataUri(d) : buildPlaceholderPdfDataUri(d);
    openModal({
      title: d.nom,
      size: "viewer",
      body: image ? (
        <img src={src} alt={d.nom} style={{ maxWidth: "100%", height: "auto", borderRadius: "var(--radius)", border: "1px solid var(--border)" }} />
      ) : (
        <iframe
          title={d.nom}
          src={src}
          style={{ width: "100%", height: "100%", border: "1px solid var(--border)", borderRadius: "var(--radius)" }}
        />
      ),
    });
  };

  const changeTab = (tab) => {
    setActiveTab(tab);
    const labels = { entreprise: "Entreprise", gouvernance: "Gouvernance", finance: "Finance" };
    toast("Onglet " + labels[tab], "info", { duration: 1200 });
  };

  return (
    <>
      <div className="page-header">
        <div>
          <div className="breadcrumb">
            <a onClick={() => navigate("/etablissements")}>Établissements</a>
            <i className="fa-solid fa-chevron-right"></i>{e.nom}
          </div>
          <h1 className="page-title">{e.nom}</h1>
          <div className="page-sub"><span className="mono">{e.id}</span> · Inscrit le {date(e.inscription)}</div>
        </div>
        <div className="page-actions">
          <button className="btn" data-testid="btn-message-etab" onClick={openMessage}><i className="fa-regular fa-envelope"></i> Envoyer un message</button>
          <button className="btn" data-testid="btn-request-doc" onClick={openRequestDoc}><i className="fa-solid fa-file-arrow-up"></i> Demander un document</button>
          {e.statut !== ESTABLISHMENT_STATUS.ACTIVATED ? (
            <button className="btn btn-success" data-testid="btn-activate" onClick={activate}><i className="fa-solid fa-check"></i> Activer</button>
          ) : (
            <button className="btn btn-danger" data-testid="btn-suspend" onClick={suspend}><i className="fa-solid fa-ban"></i> Suspendre</button>
          )}
        </div>
      </div>

      <div style={{ marginBottom: 16 }}><StatusBadge statut={e.statut} /></div>

      <div className="detail-grid">
        <div>
          <div className="card">
            <div className="tabs">
              <div className={`tab${activeTab === "entreprise" ? " active" : ""}`} data-testid="tab-entreprise" onClick={() => changeTab("entreprise")}>Entreprise</div>
              <div className={`tab${activeTab === "gouvernance" ? " active" : ""}`} data-testid="tab-gouvernance" onClick={() => changeTab("gouvernance")}>Gouvernance</div>
              <div className={`tab${activeTab === "finance" ? " active" : ""}`} data-testid="tab-finance" onClick={() => changeTab("finance")}>Finance</div>
            </div>
            <div className="card-body">
              {activeTab === "entreprise" ? (
                <div className="info-list">
                  <div><div className="k">Nom commercial</div><div className="v">{e.nom}</div></div>
                  <div><div className="k">Raison sociale</div><div className="v">{kyc.company.legalName}</div></div>
                  <div><div className="k">Forme juridique</div><div className="v">{kyc.company.legalForm}</div></div>
                  <div><div className="k">ICE</div><div className="v">{kyc.company.ice}</div></div>
                  <div><div className="k">Identifiant fiscal</div><div className="v">{kyc.company.taxIdentifier}</div></div>
                  <div><div className="k">Taxe professionnelle</div><div className="v">{kyc.company.professionalTax}</div></div>
                  <div><div className="k">Numéro RC</div><div className="v">{kyc.company.rcNumber}</div></div>
                  <div style={{ gridColumn: "1 / -1" }}><div className="k">Siège social</div><div className="v">{kyc.company.registeredOffice}</div></div>
                  <div><div className="k">Numéro de licence</div><div className="v">{kyc.company.licenseNumber} — {kyc.company.licenseIssueDate}</div></div>
                  <div style={{ gridColumn: "1 / -1" }}>
                    <div className="k">Assurance RC pro</div>
                    <div className="v">{kyc.company.insurance.company} ({kyc.company.insurance.policyNumber} — exp. {kyc.company.insurance.expiryDate})</div>
                  </div>
                  <div style={{ gridColumn: "1 / -1" }}>
                    <div className="k">Activités</div>
                    <div className="v">{kyc.company.activities.length ? kyc.company.activities.map(activityLabel).join(", ") : "—"}</div>
                  </div>
                </div>
              ) : null}

              {activeTab === "gouvernance" ? (
                <div className="info-list">
                  <div><div className="k">Représentant légal</div><div className="v">{kyc.governance.manager}</div></div>
                  <div><div className="k">Nationalité</div><div className="v">{kyc.governance.nationality}</div></div>
                  <div><div className="k">Part de capital</div><div className="v">{kyc.governance.ownershipPercentage} %</div></div>
                  <div><div className="k">Pièce d'identité</div><div className="v">{kyc.governance.identityDocumentType} · exp. {kyc.governance.identityExpiryDate}</div></div>
                  <div style={{ gridColumn: "1 / -1" }}><div className="k">Email professionnel</div><div className="v">{kyc.governance.professionalEmail}</div></div>
                </div>
              ) : null}

              {activeTab === "finance" ? (
                <div className="info-list">
                  <div style={{ gridColumn: "1 / -1" }}><div className="k">Banque</div><div className="v">{kyc.finance.bankName}</div></div>
                  <div style={{ gridColumn: "1 / -1" }}><div className="k">Titulaire du compte</div><div className="v">{kyc.finance.accountHolder}</div></div>
                  <div style={{ gridColumn: "1 / -1" }}><div className="k">RIB</div><div className="v mono strong">{kyc.finance.rib}</div></div>
                </div>
              ) : null}
            </div>
          </div>

          <div style={{ height: 16 }}></div>

          <div className="card">
            <div className="card-header"><h3 className="card-title">Documents transmis</h3><span className="card-sub">{docs.length} document{docs.length > 1 ? "s" : ""}</span></div>
            <div className="card-body">
              {docs.map((d, i) => (
                <div className="doc-row" key={i}>
                  <div className="doc-ico"><i className="fa-solid fa-file-pdf"></i></div>
                  <div className="doc-meta"><div className="doc-title">{d.nom}</div><div className="doc-sub">{d.type} · {d.taille}</div></div>
                  <StatusBadge statut={d.statut} />
                  <div className="doc-actions">
                    <button className="icon-btn" title="Visualiser" data-testid="doc-view" onClick={() => viewDoc(d)}><i className="fa-regular fa-eye"></i></button>
                    <button className="icon-btn" title="Télécharger" data-testid="doc-download" onClick={() => { downloadDoc(d); toast("Téléchargement démarré", "info"); }}><i className="fa-solid fa-download"></i></button>
                    <button className="icon-btn" title="Valider la conformité" data-testid="doc-validate" onClick={() => toast("Document validé", "success")}><i className="fa-solid fa-check" style={{ color: "var(--success)" }}></i></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <aside>
          <div className="card">
            <div className="card-header"><h3 className="card-title">Portefeuille</h3></div>
            <div className="card-body">
              <div className={`balance ${balanceClass}`} style={{ marginBottom: 12 }}>
                <span className="ico" style={{ background: balanceIcoBg, color: "#fff" }}>
                  <i className={`fa-solid ${balanceIco}`} style={{ fontSize: 10 }}></i>
                </span>
                <span className="mono">{money(solde)}</span>
              </div>
              <button className="btn" style={{ width: "100%" }} onClick={() => navigate("/finance")} data-testid="link-finance"><i className="fa-solid fa-wallet"></i> Voir la finance</button>
            </div>
          </div>
        </aside>
      </div>
    </>
  );
}

export default Etablissements;
